import { ChevronsUpDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { UserMenu } from "@/components/layout/UserMenu";
import { Avatar } from "@/components/ui/Avatar";
import type { SessionUser } from "@/lib/api/types";
import { cn } from "@/lib/utils";

const ROLE_LABELS: Record<string, string> = {
  owner: "Dueño",
  admin: "Administrador",
  agent: "Agente",
};

export function SidebarUserCard({ user, compact }: { user: SessionUser; compact: boolean }) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const displayName = user.full_name ?? user.username;

  // Un menú que solo se cierra con su propio botón queda tapando la pantalla.
  useEffect(() => {
    if (!open) return;
    function away(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={container} className="border-rail-border relative border-t p-2.5">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={compact ? displayName : undefined}
        className={cn(
          "hover:bg-rail-hover rounded-chip flex w-full cursor-pointer items-center gap-2.5 p-2 text-left",
          compact && "justify-center",
          open && "bg-rail-hover",
        )}
      >
        <Avatar name={displayName} tone="team" size={32} />
        {!compact && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium">{displayName}</span>
              <span className="text-rail-muted block text-[11px]">
                {ROLE_LABELS[user.role] ?? user.role}
              </span>
            </span>
            <ChevronsUpDown
              aria-hidden
              strokeWidth={1.75}
              className="text-rail-muted size-[15px]"
            />
          </>
        )}
      </button>
      {open && <UserMenu user={user} />}
    </div>
  );
}
